import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableHead, 
  TableHeader, 
  TableRow,
  TableCell
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ChevronDown, ChevronUp } from "lucide-react";
import { Quote, SortField, SortOrder } from "@/types/quote";
import { ScrollArea } from "@/components/ui/scroll-area";
import { QuoteListItem } from "./QuoteListItem";
import React from "react";

interface QuoteListProps {
  quotes: Quote[];
  selectedQuotes: string[];
  onSelectQuote: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onEdit: (quote: Quote) => void;
  onDelete: (quote: Quote) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField, order: SortOrder) => void;
  showDuplicateHighlight?: boolean;
}

export function QuoteList({
  quotes,
  selectedQuotes,
  onSelectQuote,
  onSelectAll,
  onEdit,
  onDelete,
  currentPage,
  totalPages,
  onPageChange,
  sortField,
  sortOrder,
  onSort,
  showDuplicateHighlight = false
}: QuoteListProps) {
  const handleSort = (field: SortField) => {
    const newOrder = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    onSort(field, newOrder);
  };

  const isDuplicate = (quote: Quote) => {
    if (!showDuplicateHighlight) return false;
    return quotes.some(
      (q) => 
        q.id !== quote.id && 
        q.content.toLowerCase().trim() === quote.content.toLowerCase().trim()
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <ScrollArea className="h-[calc(100vh-300px)]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      quotes.length > 0 &&
                      quotes.every((quote) => selectedQuotes.includes(quote.id))
                    }
                    onCheckedChange={(checked) => onSelectAll(!!checked)}
                  />
                </TableHead>
                <TableHead 
                  className="cursor-pointer"
                  onClick={() => handleSort('content')}
                >
                  Content
                  {sortField === 'content' && (
                    sortOrder === 'asc' ? <ChevronUp className="inline ml-1" /> : <ChevronDown className="inline ml-1" />
                  )}
                </TableHead>
                <TableHead 
                  className="cursor-pointer"
                  onClick={() => handleSort('author')}
                >
                  Author
                  {sortField === 'author' && (
                    sortOrder === 'asc' ? <ChevronUp className="inline ml-1" /> : <ChevronDown className="inline ml-1" />
                  )}
                </TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center h-24">
                    No quotes found
                  </TableCell>
                </TableRow>
              ) : (
                quotes.map((quote) => (
                  <QuoteListItem
                    key={quote.id}
                    quote={quote}
                    isSelected={selectedQuotes.includes(quote.id)}
                    onSelect={(checked) => onSelectQuote(quote.id, checked)}
                    onEdit={() => onEdit(quote)}
                    onDelete={() => onDelete(quote)}
                    isDuplicate={isDuplicate(quote)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
